import { useEffect, useState } from 'react';
import { EventInput } from '@fullcalendar/core';
import { moduleClientFactory } from '../../../../../api-service/module-client-factory';
import { useServiceProviderCitizenContext } from '../../../citizen-context';
import { mapCalendarEvents } from './mappers/calendar-events-mapper';
import type { RecurrencePattern } from '../../../../../components/calendar/recurrence/recurrence-pattern';
import type { UpdateServiceProviderEventValues } from '../dialogs/event-dialog-types';

const calendarClient =
  moduleClientFactory.createCitizenServiceProviderCalendarClient(true);

export function useGetCalendarEvents(
  visibleRange: { start: Date; end: Date } | null
) {
  const { citizenId, currentServiceProvider } =
    useServiceProviderCitizenContext();
  const [events, setEvents] = useState<EventInput[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  async function loadCalendarEvents() {
    if (!citizenId) {
      setEvents([]);
      return;
    }

    if (!visibleRange) {
      setEvents([]);
      return;
    }

    setIsLoading(true);
    try {
      const response =
        await calendarClient.getServiceProviderCitizenCalendarEvents({
          citizenId,
          fromDate: visibleRange.start,
          toDate: visibleRange.end,
        });

      const loadedEvents = response.calendarEvents ?? [];
      const mappedEvents = mapCalendarEvents(
        loadedEvents,
        citizenId,
        currentServiceProvider?.id
      );
      setEvents(mappedEvents);
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    if (!citizenId) {
      setEvents([]);
      return;
    }

    if (!visibleRange) {
      setEvents([]);
      return;
    }

    loadCalendarEvents();
  }, [
    citizenId,
    currentServiceProvider?.id,
    visibleRange?.start.getTime(),
    visibleRange?.end.getTime(),
  ]);

  async function createEvent(eventData: {
    serviceProviderUnitId: string;
    title: string;
    locationAddress: string;
    startAt: Date | string;
    endAt: Date | string;
    allDay: boolean;
    content?: string;
    responsibleUserId?: string;
    consumedMinutes?: number;
    recurrence?: RecurrencePattern;
    noteTypeId?: string;
  }) {
    if (!citizenId) {
      return;
    }

    await calendarClient.createServiceProviderCalendarEvent({
      citizenId,
      ...eventData,
    });
    await loadCalendarEvents();
  }

  async function updateEvent(eventData: {
    eventId: string;
    title: string;
    locationAddress: string;
    startAt: Date | string;
    endAt: Date | string;
    allDay: boolean;
    content?: string;
    responsibleUserId: string;
    consumedMinutes?: number;
    recurrence?: RecurrencePattern;
    noteTypeId?: string;
  }) {
    if (!citizenId) {
      return;
    }

    await calendarClient.updateServiceProviderCalendarEvent({
      citizenId,
      ...eventData,
    });
    await loadCalendarEvents();
  }

  async function markDone(
    eventId: string,
    comment?: string,
    consumedMinutes?: number
  ) {
    if (!citizenId) {
      return;
    }

    await calendarClient.markServiceProviderCalendarEventDone({
      citizenId,
      eventId,
      comment,
      consumedMinutes,
    });
    await loadCalendarEvents();
  }

  async function cancelEvent(
    eventId: string,
    comment?: string,
    consumedMinutes?: number
  ) {
    if (!citizenId) {
      return;
    }

    await calendarClient.cancelServiceProviderCalendarEvent({
      citizenId,
      eventId,
      comment,
      consumedMinutes,
    });
    await loadCalendarEvents();
  }

  async function unmarkEvent(eventId: string) {
    if (!citizenId) {
      return;
    }

    await calendarClient.unmarkServiceProviderCalendarEvent({
      citizenId,
      eventId,
    });
    await loadCalendarEvents();
  }

  async function deleteEvent(eventId: string) {
    if (!citizenId) {
      return;
    }

    await calendarClient.deleteServiceProviderCalendarEvent({
      citizenId,
      eventId,
    });
    await loadCalendarEvents();
  }

  async function updateOccurrence(
    values: UpdateServiceProviderEventValues,
    occurrenceStartAt: Date
  ) {
    if (!citizenId) {
      return;
    }

    await calendarClient.updateServiceProviderCalendarSeriesOccurrence({
      citizenId,
      seriesId: values.eventId,
      occurrenceStartAt,
      title: values.title,
      locationAddress: values.locationAddress,
      startAt: values.startAt,
      endAt: values.endAt,
      allDay: values.allDay,
      content: values.content,
      responsibleUserId: values.responsibleUserId,
      consumedMinutes: values.consumedMinutes,
    });
    await loadCalendarEvents();
  }

  async function markOccurrenceDone(
    seriesId: string,
    occurrenceStartAt: Date,
    comment?: string,
    consumedMinutes?: number
  ) {
    if (!citizenId) {
      return;
    }

    await calendarClient.markServiceProviderCalendarSeriesOccurrenceDone({
      citizenId,
      seriesId,
      occurrenceStartAt,
      comment,
      consumedMinutes,
    });
    await loadCalendarEvents();
  }

  async function cancelOccurrence(
    seriesId: string,
    occurrenceStartAt: Date,
    comment?: string,
    consumedMinutes?: number
  ) {
    if (!citizenId) {
      return;
    }

    await calendarClient.cancelServiceProviderCalendarSeriesOccurrence({
      citizenId,
      seriesId,
      occurrenceStartAt,
      comment,
      consumedMinutes,
    });
    await loadCalendarEvents();
  }

  async function deleteOccurrence(seriesId: string, occurrenceStartAt: Date) {
    if (!citizenId) {
      return;
    }

    await calendarClient.deleteServiceProviderCalendarSeriesOccurrence({
      citizenId,
      seriesId,
      occurrenceStartAt,
    });
    await loadCalendarEvents();
  }

  return {
    events,
    isLoading,
    createEvent,
    updateEvent,
    markDone,
    cancelEvent,
    unmarkEvent,
    deleteEvent,
    seriesOccurrenceCallbacks: {
      updateOccurrence,
      markOccurrenceDone,
      cancelOccurrence,
      deleteOccurrence,
    },
    reload: loadCalendarEvents,
  };
}
